import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  useGetUserAppSettings,
  useInvalidateUserAppSettings,
} from "state/queries/useGetUserAppSettings";
import { useMutation } from "@tanstack/react-query";
import useNotificationHandler from "hooks/useNotificationHandler";
import { MUTATION_KEYS } from "library/shared";
import { APP_ID, APP_SETTING_ID } from "library/constants";
import {
  EAppSettings,
  IUserSettings,
  IUserSettingsPayload,
} from "library/types";
import api from "service/api";
import i18n from "i18next";

interface AppSettingsContextType {
  settings: IUserSettings[];
  isLoading: boolean;
  error: Error | null;
  getSetting: (settingName: EAppSettings) => IUserSettings[];
  getSettingValue: (settingName: EAppSettings) => IUserSettings | null;
  updateSetting: (
    settingName: EAppSettings,
    value: string,
    isDefault?: boolean
  ) => void;
  updateSettingOptimistic: (
    settingName: EAppSettings,
    value: string,
    isDefault?: boolean
  ) => void;
  isInitialized: boolean;
  hasAllSettings: boolean;
  // Expose mutations for direct use
  mutations: {
    addMutation: any;
    updateMutation: any;
    deleteMutation: any;
  };
}

const AppSettingsContext = createContext<AppSettingsContextType | undefined>(
  undefined
);

interface AppSettingsProviderProps {
  children: ReactNode;
}

// Default settings that should exist for all users
const DEFAULT_APP_SETTINGS = [
  { settingName: EAppSettings.theme, defaultValue: "main", isDefault: true },
  {
    settingName: EAppSettings.darkMode,
    defaultValue: "false",
    isDefault: true,
  },
] as const;

// Settings that should only have a single value per user
const SINGLE_VALUE_SETTINGS: EAppSettings[] = [
  EAppSettings.theme,
  EAppSettings.darkMode,
  EAppSettings.language,
];

// API functions for app settings
const addAppSettings = async (payload: IUserSettingsPayload[]) => {
  const response = await api.post(`/uad/addappsettings`, payload);
  return response.data as IUserSettings[];
};

const updateAppSettings = async (payload: IUserSettings[]) => {
  const response = await api.patch(`/uad/updateappsettings`, payload);
  return response.data as IUserSettings[];
};

const deleteAppSettings = async (payload: number[]) => {
  const response = await api.delete(`/uad/deleteappsettings`, {
    data: payload,
  });
  return response.data as IUserSettings[];
};

export function AppSettingsProvider({ children }: AppSettingsProviderProps) {
  // console.log("AppSettingsProvider rendering with APP_ID:", APP_ID);

  // Load ALL app settings at startup - this is the HBH pattern
  // Make sure enabled is true and APP_ID is valid
  const {
    data: serverAppSettings = [],
    isLoading,
    error,
    isFetched,
  } = useGetUserAppSettings({
    appId: APP_ID,
    enabled: true, // Explicitly enable the query
  });

  // console.log("useGetUserAppSettings result:", {
  //   serverAppSettings,
  //   isLoading,
  //   error,
  //   isFetched,
  //   appId: APP_ID,
  // });

  const invalidateUserAppSettings = useInvalidateUserAppSettings();
  const { handleAddAxiosErrorNotification } = useNotificationHandler();

  // Mutations for API operations
  const addMutation = useMutation({
    mutationFn: addAppSettings,
    onSuccess: (updatedSettings) => {
      // Immediately update local state with the returned data
      // Create new array reference to ensure React detects the change
      setLocalSettings([...updatedSettings]);
      // Also invalidate to ensure consistency
      invalidateUserAppSettings();
    },
    onError: (error) => handleAddAxiosErrorNotification(error),
  });

  const updateMutation = useMutation({
    mutationFn: updateAppSettings,
    onSuccess: (updatedSettings) => {
      // Immediately update local state with the returned data
      // Create new array reference to ensure React detects the change
      setLocalSettings([...updatedSettings]);
      // Also invalidate to ensure consistency
      invalidateUserAppSettings();
    },
    onError: (error) => handleAddAxiosErrorNotification(error),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAppSettings,
    onSuccess: (updatedSettings) => {
      // Immediately update local state with the returned data
      // Create new array reference to ensure React detects the change
      setLocalSettings([...updatedSettings]);
      // Also invalidate to ensure consistency
      invalidateUserAppSettings();
    },
    onError: (error) => handleAddAxiosErrorNotification(error),
  });

  // Local state for optimistic updates and complete settings management
  const [localSettings, setLocalSettings] = useState<IUserSettings[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasAllSettings, setHasAllSettings] = useState(false);

  // Track if we've already created missing settings to prevent infinite loop
  const hasCreatedMissingSettings = useRef(false);

  // Helper function to set app settings
  const setAppSetting = useCallback(
    (settingName: EAppSettings, value: string, isDefault = false) => {
      if (!serverAppSettings) return;

      // Find all existing settings of this type
      const existingSettings = serverAppSettings.filter(
        (setting) => setting.userAppSettingName === settingName
      );

      const isSingleValueSetting = SINGLE_VALUE_SETTINGS.includes(settingName);

      if (isSingleValueSetting) {
        // For single-value settings (Theme, Dark_Mode), enforce uniqueness
        if (existingSettings.length > 1) {
          // Clean up duplicates first
          const settingToKeep =
            existingSettings.find((s) => s.isDefault) ||
            existingSettings.reduce((latest, current) =>
              (current.userAppSettingId || 0) > (latest.userAppSettingId || 0)
                ? current
                : latest
            );

          const settingsToDelete = existingSettings
            .filter(
              (s) => s.userAppSettingId !== settingToKeep.userAppSettingId
            )
            .map((s) => s.userAppSettingId)
            .filter((id): id is number => id !== undefined);

          if (settingsToDelete.length > 0) {
            deleteMutation.mutate(settingsToDelete);
          }

          // Update the remaining setting - pass as array
          updateMutation.mutate([
            {
              ...settingToKeep,
              userAppSettingValue: value,
              isDefault,
            },
          ]);
        } else if (existingSettings.length === 1) {
          // Update the single existing setting - pass as array
          updateMutation.mutate([
            {
              ...existingSettings[0],
              userAppSettingValue: value,
              isDefault,
            },
          ]);
        } else {
          // Create new single-value setting - pass array directly
          addMutation.mutate([
            {
              applicationCodeId: APP_ID,
              appSettingCodeId: APP_SETTING_ID,
              userAppSettingName: settingName,
              userAppSettingValue: value,
              isDefault,
            },
          ]);
        }
      } else {
        // For multi-value settings, always create new entries - pass array directly
        addMutation.mutate([
          {
            applicationCodeId: APP_ID,
            appSettingCodeId: APP_SETTING_ID,
            userAppSettingName: settingName,
            userAppSettingValue: value,
            isDefault,
          },
        ]);
      }
    },
    [serverAppSettings, addMutation, updateMutation, deleteMutation]
  );

  // Helper function to cleanup duplicate settings
  const manualCleanupDuplicates = useCallback(() => {
    if (!serverAppSettings) return [];

    const results: any[] = [];

    SINGLE_VALUE_SETTINGS.forEach((settingName) => {
      const settingsOfType = serverAppSettings.filter(
        (setting) => setting.userAppSettingName === settingName
      );

      if (settingsOfType.length > 1) {
        const settingToKeep =
          settingsOfType.find((s) => s.isDefault) ||
          settingsOfType.reduce((latest, current) =>
            (current.userAppSettingId || 0) > (latest.userAppSettingId || 0)
              ? current
              : latest
          );

        const settingsToDelete = settingsOfType.filter(
          (s) => s.userAppSettingId !== settingToKeep.userAppSettingId
        );

        if (settingsToDelete.length > 0) {
          const settingIdsToDelete = settingsToDelete
            .map((s) => s.userAppSettingId)
            .filter((id): id is number => id !== undefined);

          deleteMutation.mutate(settingIdsToDelete);

          results.push({
            settingName,
            duplicatesFound: settingsOfType.length,
            duplicatesDeleted: settingIdsToDelete,
            settingKept: settingToKeep.userAppSettingId!,
          });
        }
      }
    });

    return results;
  }, [serverAppSettings, deleteMutation]);

  // Sync server settings to local state
  useEffect(() => {
    // console.log("AppSettingsProvider useEffect triggered:", {
    //   isLoading,
    //   serverAppSettings: serverAppSettings?.length,
    //   isFetched,
    // });

    if (!isLoading && isFetched) {
      setLocalSettings(serverAppSettings);
      setHasInitialized(true);

      // Check if all required default settings exist
      const missingSettings = DEFAULT_APP_SETTINGS.filter(
        (defaultSetting) =>
          !serverAppSettings.some(
            (setting) =>
              setting.userAppSettingName === defaultSetting.settingName
          )
      );

      // console.log("Missing settings:", missingSettings);
      setHasAllSettings(missingSettings.length === 0);

      // Only create missing settings once to prevent infinite loop
      if (missingSettings.length > 0 && !hasCreatedMissingSettings.current) {
        hasCreatedMissingSettings.current = true;

        // console.log(
        //   `Creating ${missingSettings.length} missing default settings:`,
        //   missingSettings.map((s) => s.settingName)
        // );

        // Create missing settings
        missingSettings.forEach((missingSetting) => {
          setAppSetting(
            missingSetting.settingName,
            missingSetting.defaultValue,
            missingSetting.isDefault
          );
        });
      }
    }
  }, [serverAppSettings, isLoading, isFetched, setAppSetting]); // Added setAppSetting to dependencies

  // Run cleanup on initial load
  useEffect(() => {
    if (hasInitialized && localSettings.length > 0) {
      const cleanupResults = manualCleanupDuplicates();
      if (cleanupResults.length > 0) {
        // console.log("App Settings duplicates cleaned up:", cleanupResults);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInitialized, localSettings.length]);

  // Update i18n language when settings are loaded
  useEffect(() => {
    if (hasInitialized && localSettings.length > 0) {
      const languageSetting = localSettings.find(
        (setting) => setting.userAppSettingName === EAppSettings.language
      );

      if (languageSetting?.userAppSettingValue) {
        const currentLang = i18n.language;
        const newLang = languageSetting.userAppSettingValue;

        if (currentLang !== newLang) {
          i18n.changeLanguage(newLang);
        }
      }
    }
  }, [hasInitialized, localSettings]);

  const updateSettingOptimistic = React.useCallback(
    (settingName: EAppSettings, value: string, isDefault = false) => {
      // 1. Update local state immediately for instant UI feedback
      setLocalSettings((prevSettings) => {
        const existingSetting = prevSettings.find(
          (s) => s.userAppSettingName === settingName
        );

        if (existingSetting) {
          // Update existing setting
          return prevSettings.map((s) =>
            s.userAppSettingName === settingName
              ? { ...s, userAppSettingValue: value, isDefault }
              : s
          );
        } else {
          // Add new setting (this should be rare with proper initialization)
          const newSetting: IUserSettings = {
            userAppSettingId: Date.now(), // Temporary ID for optimistic update
            applicationCodeId: APP_ID,
            userAppSettingName: settingName,
            userAppSettingValue: value,
            isDefault,
          };
          return [...prevSettings, newSetting];
        }
      });

      // 2. Update server in background
      setAppSetting(settingName, value, isDefault);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setAppSetting]
  );

  const getSettingFromLocal = React.useCallback(
    (settingName: EAppSettings): IUserSettings[] => {
      return localSettings.filter(
        (setting) => setting.userAppSettingName === settingName
      );
    },
    [localSettings]
  );

  const getSettingValueFromLocal = React.useCallback(
    (settingName: EAppSettings): IUserSettings | null => {
      const settings = getSettingFromLocal(settingName);
      // Prefer default settings, then most recent
      return settings.find((s) => s.isDefault) || settings[0] || null;
    },
    [getSettingFromLocal]
  );

  const contextValue: AppSettingsContextType = React.useMemo(
    () => ({
      settings: localSettings,
      isLoading,
      error: error as Error | null,
      getSetting: getSettingFromLocal,
      getSettingValue: getSettingValueFromLocal,
      updateSetting: setAppSetting, // Pessimistic update
      updateSettingOptimistic, // Optimistic update
      isInitialized: hasInitialized && hasAllSettings, // Only initialized when we have all required settings
      hasAllSettings,
      // Expose mutations for direct use
      mutations: {
        addMutation,
        updateMutation,
        deleteMutation,
      },
    }),
    [
      localSettings,
      isLoading,
      error,
      getSettingFromLocal,
      getSettingValueFromLocal,
      setAppSetting,
      updateSettingOptimistic,
      hasInitialized,
      hasAllSettings,
      addMutation,
      updateMutation,
      deleteMutation,
    ]
  );

  return (
    <AppSettingsContext.Provider value={contextValue}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export function useAppSettingsContext(): AppSettingsContextType {
  const context = useContext(AppSettingsContext);
  if (context === undefined) {
    throw new Error(
      "useAppSettingsContext must be used within an AppSettingsProvider"
    );
  }
  return context;
}
